import SERVICES_API_V1 from 'api/services/v1'
import SERVICES_API_V2 from 'api/services/v2'
import i18n from 'i18next'
import omit from 'lodash/omit'
import { call, debounce, put, select, takeLatest } from 'redux-saga/effects'
import { cancellableCall } from 'store/saga-utils/cancellableCall.saga'
import { confirmSaga } from 'store/saga-utils/confirm.saga'
import extractErrors from 'utils/extractErrors'
import { notificationShow } from 'modules/Notifications/actions'
import {
  ACCOUNTS_ARCHIVE_TOGGLE,
  FETCH_NETWORK_ACCOUNTS,
  FETCH_NETWORK_DATA,
  FETCH_NETWORK_TEXT,
  NETWORK_QUERY_TYPE,
  PROCESS_NETWORK_GROUP_ACTION_WITH_CONFIRM,
  REMOVE_AND_FETCH_NETWORK_ACCOUNTS,
  REMOVE_NETWORK_ACCOUNTS,
  SET_NETWORK_ACCOUNTS,
  SET_NETWORK_ACCOUNTS_QUERY,
  SET_NETWORK_DATA,
  SET_NETWORK_TEXT,
  SET_SELECTION,
  TOGGLE_PROCESSING,
  UPDATE_ACCOUNT_BY_ID,
  UPDATE_ACCOUNT_NAME,
  UPDATE_ACCOUNTS,
  UPDATE_NETWORK_ACCOUNTS_STATUS,
} from 'modules/Services/types'

const prepareResults = (response, accounts) =>
  response.reduce(
    (acc, { id: resultId, status }) => {
      const { login = resultId, id } = accounts.find(({ id }) => id === resultId) ?? {}

      if (status === 'success') {
        acc.success = [...acc.success, { id, login }]
      } else {
        acc.failed = [...acc.failed, { id, login }]
      }
      return acc
    },
    { success: [], failed: [] },
  )

function* fetchNetworkData({ payload }) {
  yield put({
    type: SET_NETWORK_DATA,
    payload: { id: payload.id },
  })

  try {
    yield put({
      type: SET_NETWORK_DATA,
      payload: { loading: true },
    })

    const { response, error, canceled } = yield cancellableCall(
      SERVICES_API_V1.getNetworkData,
      {
        networkId: payload.id,
      },
      arguments,
    )

    if (canceled) return

    if (error) {
      throw error
    }

    if (response?.data) {
      yield put({
        type: SET_NETWORK_DATA,
        payload: {
          ...response.data,
          loading: false,
        },
      })
    }
  } catch (error) {
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_fetch_failed',
      }),
    )
  } finally {
    yield put({
      type: SET_NETWORK_DATA,
      payload: { loading: false },
    })
  }
}

function* fetchNetworkAccounts({ payload = {} }) {
  const { params = {} } = payload

  try {
    yield put({
      type: UPDATE_ACCOUNTS,
      payload: { loading: true },
    })

    const {
      accounts: { composedParams = {} },
    } = yield select((state) => state['services/network'])

    const { response, error } = yield call(SERVICES_API_V2.getNetworkAccounts, {
      params: { ...composedParams, ...params },
    })

    if (error) {
      throw error
    }

    yield put({
      type: SET_NETWORK_ACCOUNTS,
      payload: omit(response, ['status']),
    })
  } catch (error) {
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_fetch_failed',
      }),
    )
    yield put({
      type: UPDATE_ACCOUNTS,
      payload: { loading: false },
    })
    console.logXHR(error)
  }
}

function* fetchNetworkText({ payload }) {
  try {
    const { response, error } = yield call(SERVICES_API_V1.getNetworkText, payload)

    if (error || !response.data) {
      throw new Error('service text loading failed (' + payload.textKey + ')')
    }

    yield put({
      type: SET_NETWORK_TEXT,
      payload: { key: payload.textKey, ...response.data },
    })
  } catch (error) {
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_fetch_failed',
      }),
    )
    yield put({ type: 'MODAL/HIDE', payload: { modalType: 'networkText' } })
  }
}

function* typeQuery({ payload }) {
  yield put({ type: SET_NETWORK_ACCOUNTS_QUERY, payload })
}

function* processResultsNotification({ processResults, action }) {
  if (processResults.success.length > 0) {
    yield put(
      notificationShow({
        type: 'success',
        message: i18n.t(`services::account_list.group_notifications.${action}.success`, {
          login: processResults.success.map(({ login }) => login).join(', '),
        }),
      }),
    )
  }

  if (processResults.failed.length > 0) {
    yield put(
      notificationShow({
        type: 'error',
        message: i18n.t(`services::account_list.group_notifications.${action}.error`, {
          login: processResults.failed.map(({ login }) => login).join(', '),
        }),
      }),
    )
  }
}

function* processNetworkSelection({ payload }) {
  const { accounts } = yield select((state) => state['services/network'])

  try {
    yield put({
      type: TOGGLE_PROCESSING,
      payload: { processing: true },
    })

    const { response, error, canceled } = yield cancellableCall(
      payload.API_CALL,
      {
        data: { ids: payload.ids.map((id) => +id) },
      },
      arguments,
    )

    if (canceled) return

    if (error) {
      throw error
    }

    const processResults = prepareResults(response.data, accounts.data)

    yield call(processResultsNotification, {
      processResults,
      action: payload.groupActionType,
    })

    return { response, undefined }
  } catch (error) {
    const { form } = extractErrors(error.errors)
    yield put(
      notificationShow({
        type: 'error',
        message: form || 'common::notifications.data_update_failed',
      }),
    )
    return { undefined, error }
  } finally {
    yield put({
      type: TOGGLE_PROCESSING,
      payload: { processing: false },
    })
  }
}

function* processNetworkGroupActionWithConfirm({
  payload,
  meta: { resolve = () => {}, reject = () => {} } = {},
}) {
  try {
    const [confirmed, UUID] = yield call(confirmSaga, {
      modalType: 'confirmation',
      ...payload,
    })

    yield put({ type: 'MODAL/HIDE', payload: { UUID } })

    if (confirmed) {
      const { response, error } = yield call(processNetworkSelection, {
        payload,
      })

      if (error) {
        throw error
      }

      resolve(response)
    }
  } catch (error) {
    reject(error)
    console.logXHR(error)
  }
}

function* toggleArchive({ payload, meta: { resolve = () => {}, reject = () => {} } = {} }) {
  const { accounts } = yield select((state) => state['services/network'])

  const METHOD = payload.toArchive ? 'archiveNetworkAccountsByIDs' : 'unarchiveNetworkAccountsByIDs'

  try {
    const { response, error } = yield call(SERVICES_API_V2[METHOD], {
      data: {
        ids: payload.ids,
      },
    })

    if (error) {
      throw error
    }

    yield put({
      type: UPDATE_NETWORK_ACCOUNTS_STATUS,
      payload: {
        needRemoveAccounts: (filters) =>
          payload.toArchive ? !filters?.is_archived : filters?.is_archived,
        data: response.data,
        ids: payload.ids,
      },
    })

    const processResults = prepareResults(response.data, accounts.data)

    yield call(processResultsNotification, {
      processResults,
      action: payload.toArchive ? 'group_archive' : 'group_unarchive',
    })

    resolve()
  } catch (error) {
    reject(error)
    console.logXHR(error)
  }
}

function* removeAndFetch({ payload: { data } }) {
  const { accounts } = yield select((state) => state['services/network'])

  const processedAccounts =
    data.filter(({ status }) => status === 'success').map(({ id }) => id) ?? []

  if (processedAccounts.length > 0) {
    yield put({
      type: REMOVE_NETWORK_ACCOUNTS,
      payload: processedAccounts,
    })

    yield put({
      type: FETCH_NETWORK_ACCOUNTS,
      payload: {
        params: {
          offset: accounts.data.length - processedAccounts.length,
          limit: processedAccounts.length,
        },
      },
    })
  }
}

function* updateStatus({
  payload: { data: response, needRemoveAccounts = () => false, updater = () => {} },
}) {
  const {
    accounts: { data },
    filters,
  } = yield select((state) => state['services/network'])

  if (needRemoveAccounts(filters)) {
    yield put({
      type: REMOVE_AND_FETCH_NETWORK_ACCOUNTS,
      payload: { data: response },
    })
  } else {
    const updatedAccountsData = data.map((account) => {
      const inResponse = response.find(({ id }) => id === account.id)
      return inResponse ? updater(account, inResponse) : account
    })

    yield put({
      type: UPDATE_ACCOUNTS,
      payload: { data: updatedAccountsData },
    })

    yield put({
      type: SET_SELECTION,
      payload: [],
    })
  }
}

function* updateAccountName({
  payload: { id, name },
  meta: { resolve = () => {}, reject = () => {} } = {},
}) {
  try {
    const { error } = yield call(SERVICES_API_V2.updateName, {
      accountId: id,
      data: { name },
    })

    if (error) {
      throw error
    }

    yield put({
      type: UPDATE_ACCOUNT_BY_ID,
      payload: { id, update: { name } },
    })
    resolve()
  } catch (error) {
    const { fields } = extractErrors(error.errors)

    yield put(
      notificationShow({
        type: 'error',
        message: fields?.name || 'common::notifications.data_update_failed',
      }),
    )
    reject(error)
  }
}

export default function* Sagas() {
  yield takeLatest(FETCH_NETWORK_DATA, fetchNetworkData)
  yield takeLatest(FETCH_NETWORK_ACCOUNTS, fetchNetworkAccounts)
  yield takeLatest(FETCH_NETWORK_TEXT, fetchNetworkText)
  yield debounce(500, NETWORK_QUERY_TYPE, typeQuery)
  yield takeLatest(PROCESS_NETWORK_GROUP_ACTION_WITH_CONFIRM, processNetworkGroupActionWithConfirm)
  yield takeLatest(REMOVE_AND_FETCH_NETWORK_ACCOUNTS, removeAndFetch)
  yield takeLatest(UPDATE_NETWORK_ACCOUNTS_STATUS, updateStatus)
  yield takeLatest(UPDATE_ACCOUNT_NAME, updateAccountName)
  yield takeLatest(ACCOUNTS_ARCHIVE_TOGGLE, toggleArchive)
}
